import { useCallback } from 'react';
import notify from 'devextreme/ui/notify';
import { formatTime } from '../utils/stopwatchUtils';
import { 
    saveStopwatchConfig,
    saveNamedSession,
    loadNamedSession,
    deleteNamedSession,
    updateNamedSession,
    clearStopwatchState,
    loadStopwatchState
} from '../persistence';

export const useStopwatchActions = (state) => {
    const {
        lanes, setLanes,
        rows, setRows,
        grid, setGrid,
        activeStopwatches, setActiveStopwatches,
        recordedTimes, setRecordedTimes,
        currentTimes, setCurrentTimes,
        lapTimes, setLapTimes,
        startTimes, setStartTimes,
        lapStartTimes, setLapStartTimes,
        unknownTimes, setUnknownTimes,
        selectedCell, setSelectedCell,
        swapMode, setSwapMode,
        firstSwapCell, setFirstSwapCell,
        sessionName, setSessionName,
        currentSessionId, setCurrentSessionId,
        setIsSetupMode,
        setShowAthleteSelector,
        setShowUnknownTimePopup,
        setShowExportPopup,
        setShowSaveSessionDialog,
        setShowLoadSessionDialog,
        saveCurrentState,
        loadSessions
    } = state;

    // Initialisiere Grid
    const initializeGrid = useCallback(() => {
        const newGrid = [];
        const newActiveStopwatches = {};
        const newRecordedTimes = {};
        const newCurrentTimes = {};
        const newLapTimes = {};
        const newStartTimes = {};
        const newLapStartTimes = {};

        for (let row = 0; row < rows; row++) {
            newGrid[row] = [];
            for (let lane = 0; lane < lanes; lane++) {
                newGrid[row][lane] = null;
                const key = `${row}-${lane}`;
                newActiveStopwatches[key] = false;
                newRecordedTimes[key] = [];
                newCurrentTimes[key] = 0;
                newLapTimes[key] = 0;
                newStartTimes[key] = null;
                newLapStartTimes[key] = null;
            }
        }

        setGrid(newGrid);
        setActiveStopwatches(newActiveStopwatches);
        setRecordedTimes(newRecordedTimes);
        setCurrentTimes(newCurrentTimes);
        setLapTimes(newLapTimes);
        setStartTimes(newStartTimes);
        setLapStartTimes(newLapStartTimes);
        setIsSetupMode(false);
        
        // Speichere neue Konfiguration
        saveStopwatchConfig(lanes, rows);
        
        // Speichere die neue Sitzung direkt mit dem eingegebenen Namen
        if (sessionName.trim()) {
            const state = {
                lanes,
                rows,
                grid: newGrid,
                recordedTimes: newRecordedTimes,
                unknownTimes: [],
                isSetupMode: false
            };

            const sessionId = saveNamedSession(sessionName.trim(), state);
            if (sessionId) {
                setCurrentSessionId(sessionId);
                loadSessions();
                notify(`Neue Sitzung "${sessionName.trim()}" wurde erstellt und gespeichert`, 'success');
            } else {
                notify('Grid erstellt, aber Fehler beim Speichern der Sitzung', 'warning');
            }
            setSessionName('');
        } else {
            notify('Neues Grid erstellt', 'success');
        }
    }, [lanes, rows, sessionName, setGrid, setActiveStopwatches, setRecordedTimes, setCurrentTimes, setLapTimes, setStartTimes, setLapStartTimes, setIsSetupMode, setCurrentSessionId, setSessionName, loadSessions]);

    // Starte alle Stoppuhren einer Reihe
    const startRow = useCallback((rowIndex) => {
        const now = Date.now();
        
        // Erst alle Uhren in der Reihe stoppen
        setActiveStopwatches(prev => {
            const newState = { ...prev };
            for (let lane = 0; lane < lanes; lane++) {
                const key = `${rowIndex}-${lane}`;
                newState[key] = false;
            }
            return newState;
        });

        // Timer-Zeiten und Startzeiten zurücksetzen
        setCurrentTimes(prev => {
            const newState = { ...prev };
            for (let lane = 0; lane < lanes; lane++) {
                const key = `${rowIndex}-${lane}`;
                newState[key] = 0;
            }
            return newState;
        });

        setLapTimes(prev => {
            const newState = { ...prev };
            for (let lane = 0; lane < lanes; lane++) {
                const key = `${rowIndex}-${lane}`;
                newState[key] = 0;
            }
            return newState;
        });

        setStartTimes(prev => {
            const newState = { ...prev };
            for (let lane = 0; lane < lanes; lane++) {
                const key = `${rowIndex}-${lane}`;
                newState[key] = now;
            }
            return newState;
        });

        setLapStartTimes(prev => {
            const newState = { ...prev };
            for (let lane = 0; lane < lanes; lane++) {
                const key = `${rowIndex}-${lane}`;
                newState[key] = now;
            }
            return newState;
        });

        // Dann nach kurzer Verzögerung alle wieder starten
        setTimeout(() => {
            setActiveStopwatches(prev => {
                const newState = { ...prev };
                for (let lane = 0; lane < lanes; lane++) {
                    const key = `${rowIndex}-${lane}`;
                    newState[key] = true;
                }
                return newState;
            });
        }, 50);
    }, [lanes, setActiveStopwatches, setCurrentTimes, setLapTimes, setStartTimes, setLapStartTimes]);

    // Stoppe alle Stoppuhren einer Reihe
    const stopRow = useCallback((rowIndex) => {
        setActiveStopwatches(prev => {
            const newState = { ...prev };
            for (let lane = 0; lane < lanes; lane++) {
                const key = `${rowIndex}-${lane}`;
                newState[key] = false;
            }
            return newState;
        });
    }, [lanes, setActiveStopwatches]);

    // Einzelne Stoppuhr starten/stoppen
    const toggleStopwatch = useCallback((rowIndex, laneIndex) => {
        const key = `${rowIndex}-${laneIndex}`;
        const now = Date.now();
        
        setActiveStopwatches(prev => {
            const newIsActive = !prev[key];
            
            if (newIsActive) {
                // Starten - setze Startzeiten
                setStartTimes(prevStart => ({
                    ...prevStart,
                    [key]: now - (currentTimes[key] || 0)
                }));
                setLapStartTimes(prevLapStart => ({
                    ...prevLapStart,
                    [key]: now - (lapTimes[key] || 0)
                }));
            }
            
            return {
                ...prev,
                [key]: newIsActive
            };
        });
    }, [currentTimes, lapTimes, setActiveStopwatches, setStartTimes, setLapStartTimes]);

    // Einzelne Stoppuhr neustarten
    const restartStopwatch = useCallback((rowIndex, laneIndex) => {
        const key = `${rowIndex}-${laneIndex}`;
        const now = Date.now();
        
        // Erst stoppen
        setActiveStopwatches(prev => ({
            ...prev,
            [key]: false
        }));
        
        // Zeiten zurücksetzen
        setCurrentTimes(prev => ({
            ...prev,
            [key]: 0
        }));
        
        setLapTimes(prev => ({
            ...prev,
            [key]: 0
        }));
        
        // Startzeiten setzen
        setStartTimes(prev => ({
            ...prev,
            [key]: now
        }));
        
        setLapStartTimes(prev => ({
            ...prev,
            [key]: now
        }));
        
        // Dann nach kurzer Verzögerung wieder starten
        setTimeout(() => {
            setActiveStopwatches(prev => ({
                ...prev,
                [key]: true
            }));
        }, 50);
    }, [setActiveStopwatches, setCurrentTimes, setLapTimes, setStartTimes, setLapStartTimes]);

    // Zeit aufzeichnen
    const recordTime = useCallback((rowIndex, laneIndex, time, type) => {
        const key = `${rowIndex}-${laneIndex}`;
        const timeEntry = {
            time,
            type,
            timestamp: new Date(),
            athlete: grid[rowIndex][laneIndex]
        };

        setRecordedTimes(prev => ({
            ...prev,
            [key]: [...(prev[key] || []), timeEntry]
        }));
    }, [grid, setRecordedTimes]);

    // Sportler auswählen
    const selectAthlete = useCallback((athlete) => {
        if (selectedCell) {
            const newGrid = [...grid];
            newGrid[selectedCell.row][selectedCell.lane] = athlete;
            setGrid(newGrid);
            setSelectedCell(null);
            setShowAthleteSelector(false);
        }
    }, [grid, selectedCell, setGrid, setSelectedCell, setShowAthleteSelector]);

    // Sportler tauschen
    const handleSwapAthlete = useCallback((rowIndex, laneIndex) => {
        if (!swapMode) {
            setSwapMode(true);
            setFirstSwapCell({ row: rowIndex, lane: laneIndex });
            notify('Wählen Sie die zweite Zelle zum Tauschen', 'info');
        } else {
            if (firstSwapCell.row === rowIndex && firstSwapCell.lane === laneIndex) {
                // Gleiche Zelle - Abbrechen
                setSwapMode(false);
                setFirstSwapCell(null);
                notify('Tausch abgebrochen', 'warning');
            } else {
                // Kompletten Tausch durchführen
                const newGrid = [...grid];
                const athlete1 = newGrid[firstSwapCell.row][firstSwapCell.lane];
                const athlete2 = newGrid[rowIndex][laneIndex];
                
                // Athleten tauschen
                newGrid[firstSwapCell.row][firstSwapCell.lane] = athlete2;
                newGrid[rowIndex][laneIndex] = athlete1;
                
                // Keys für Timer-Daten
                const key1 = `${firstSwapCell.row}-${firstSwapCell.lane}`;
                const key2 = `${rowIndex}-${laneIndex}`;
                
                // Alle Timer-Daten tauschen
                const swapInState = (setState) => {
                    setState(prev => {
                        const newState = { ...prev };
                        const temp = newState[key1];
                        newState[key1] = newState[key2];
                        newState[key2] = temp;
                        return newState;
                    });
                };

                swapInState(setActiveStopwatches);
                swapInState(setRecordedTimes);
                swapInState(setCurrentTimes);
                swapInState(setLapTimes);
                swapInState(setStartTimes);
                swapInState(setLapStartTimes);

                setGrid(newGrid);
                setSwapMode(false);
                setFirstSwapCell(null);
                notify('Sportler und alle Daten getauscht', 'success');
            }
        }
    }, [swapMode, firstSwapCell, grid, setSwapMode, setFirstSwapCell, setGrid, setActiveStopwatches, setRecordedTimes, setCurrentTimes, setLapTimes, setStartTimes, setLapStartTimes]);

    // Unbekannte Zeit aufzeichnen
    const recordUnknownTime = useCallback(() => {
        const currentTime = Date.now();
        const timeEntry = {
            time: currentTime,
            timestamp: new Date(),
            assigned: false
        };
        
        setUnknownTimes(prev => [...prev, timeEntry]);
        setShowUnknownTimePopup(true);
        notify('Unbekannte Zeit aufgezeichnet', 'info');
    }, [setUnknownTimes, setShowUnknownTimePopup]);

    // Zeit einem Sportler zuordnen
    const assignTimeToAthlete = useCallback((timeIndex, rowIndex, laneIndex) => {
        const time = unknownTimes[timeIndex];
        const key = `${rowIndex}-${laneIndex}`;
        const athlete = grid[rowIndex][laneIndex];
        
        if (athlete) {
            const timeEntry = {
                ...time,
                type: 'assigned',
                athlete
            };
            
            setRecordedTimes(prev => ({
                ...prev,
                [key]: [...(prev[key] || []), timeEntry]
            }));
            
            // Entferne aus unbekannten Zeiten
            setUnknownTimes(prev => prev.filter((_, index) => index !== timeIndex));
            notify(`Zeit ${formatTime(time.time)} zugeordnet an ${athlete.surname} ${athlete.lastname}`, 'success');
        }
    }, [unknownTimes, grid, setRecordedTimes, setUnknownTimes]);

    // Session Management
    const handleSaveSession = useCallback(() => {
        if (!sessionName.trim()) {
            notify('Bitte geben Sie einen Namen für die Sitzung ein', 'warning');
            return;
        }

        // Aktualisiere Timer-Zeiten vor dem Speichern
        const now = Date.now();
        const updatedCurrentTimes = { ...currentTimes };
        const updatedLapTimes = { ...lapTimes };

        Object.keys(activeStopwatches).forEach(key => {
            if (activeStopwatches[key]) {
                if (startTimes[key]) {
                    updatedCurrentTimes[key] = now - startTimes[key];
                }
                if (lapStartTimes[key]) {
                    updatedLapTimes[key] = now - lapStartTimes[key];
                }
            }
        });

        const state = {
            lanes,
            rows,
            grid,
            activeStopwatches,
            recordedTimes,
            currentTimes: updatedCurrentTimes,
            lapTimes: updatedLapTimes,
            startTimes,
            lapStartTimes,
            unknownTimes,
            isSetupMode: false
        };

        const sessionId = saveNamedSession(sessionName.trim(), state);
        if (sessionId) {
            saveCurrentState();
            setCurrentSessionId(sessionId);
            setShowSaveSessionDialog(false);
            setSessionName('');
            loadSessions();
            notify(`Sitzung "${sessionName.trim()}" wurde gespeichert`, 'success');
        } else {
            notify('Fehler beim Speichern der Sitzung', 'error');
        }
    }, [sessionName, lanes, rows, grid, activeStopwatches, recordedTimes, currentTimes, lapTimes, startTimes, lapStartTimes, unknownTimes, saveCurrentState, setCurrentSessionId, setShowSaveSessionDialog, setSessionName, loadSessions]);

    const handleLoadSession = useCallback((sessionId) => {
        const session = loadNamedSession(sessionId);
        if (session && session.state) {
            setLanes(session.state.lanes);
            setRows(session.state.rows);
            setGrid(session.state.grid);
            setRecordedTimes(session.state.recordedTimes || {});
            setUnknownTimes(session.state.unknownTimes || []);
            
            // Lade auch die Timer-States
            setActiveStopwatches(session.state.activeStopwatches || {});
            setCurrentTimes(session.state.currentTimes || {});
            setLapTimes(session.state.lapTimes || {});
            setStartTimes(session.state.startTimes || {});
            setLapStartTimes(session.state.lapStartTimes || {});
            
            setCurrentSessionId(sessionId);
            setIsSetupMode(false);
            setShowLoadSessionDialog(false);
            notify(`Sitzung "${session.name}" wurde geladen`, 'success');
        } else {
            notify('Fehler beim Laden der Sitzung', 'error');
        }
    }, [setLanes, setRows, setGrid, setRecordedTimes, setUnknownTimes, setActiveStopwatches, setCurrentTimes, setLapTimes, setStartTimes, setLapStartTimes, setCurrentSessionId, setIsSetupMode, setShowLoadSessionDialog]);

    const handleDeleteSession = useCallback((sessionId) => {
        const success = deleteNamedSession(sessionId);
        if (success) {
            if (currentSessionId === sessionId) {
                setCurrentSessionId(null);
            }
            loadSessions();
            notify('Sitzung wurde gelöscht', 'success');
        } else {
            notify('Fehler beim Löschen der Sitzung', 'error');
        }
    }, [currentSessionId, setCurrentSessionId, loadSessions]);

    const handleUpdateCurrentSession = useCallback(() => {
        if (!currentSessionId) return;

        // Aktualisiere Timer-Zeiten vor dem Speichern
        const now = Date.now();
        const updatedCurrentTimes = { ...currentTimes };
        const updatedLapTimes = { ...lapTimes };

        Object.keys(activeStopwatches).forEach(key => {
            if (activeStopwatches[key]) {
                if (startTimes[key]) {
                    updatedCurrentTimes[key] = now - startTimes[key];
                }
                if (lapStartTimes[key]) {
                    updatedLapTimes[key] = now - lapStartTimes[key];
                }
            }
        });

        const state = {
            lanes,
            rows,
            grid,
            activeStopwatches,
            recordedTimes,
            currentTimes: updatedCurrentTimes,
            lapTimes: updatedLapTimes,
            startTimes,
            lapStartTimes,
            unknownTimes,
            isSetupMode: false
        };

        const success = updateNamedSession(currentSessionId, state);
        if (success) {
            loadSessions();
            notify('Aktuelle Sitzung wurde aktualisiert', 'success');
        }
    }, [currentSessionId, lanes, rows, grid, activeStopwatches, recordedTimes, currentTimes, lapTimes, startTimes, lapStartTimes, unknownTimes, loadSessions]);

    // Weitere Action Handlers
    const handleManualSave = useCallback(() => {
        saveCurrentState();
        notify('Zustand manuell gespeichert', 'success');
    }, [saveCurrentState]);

    const handleClearSavedState = useCallback(() => {
        clearStopwatchState();
        notify('Gespeicherter Zustand gelöscht', 'warning');
    }, []);

    const handleResetToSetup = useCallback(() => {
        setIsSetupMode(true);
        notify('Zurück zum Setup-Modus', 'info');
    }, [setIsSetupMode]);

    const handleEditCell = useCallback((rowIndex, laneIndex) => {
        setSelectedCell({ row: rowIndex, lane: laneIndex });
        setShowAthleteSelector(true);
    }, [setSelectedCell, setShowAthleteSelector]);

    const handleLoadTemporaryState = useCallback(() => {
        const savedState = loadStopwatchState();
        if (savedState) {
            setLanes(savedState.lanes);
            setRows(savedState.rows);
            setGrid(savedState.grid);
            setActiveStopwatches(savedState.activeStopwatches);
            setRecordedTimes(savedState.recordedTimes);
            setCurrentTimes(savedState.currentTimes);
            setLapTimes(savedState.lapTimes);
            setStartTimes(savedState.startTimes);
            setLapStartTimes(savedState.lapStartTimes);
            setUnknownTimes(savedState.unknownTimes);
            setIsSetupMode(false);
            notify('Temporärer Zustand geladen', 'success');
        }
    }, [setLanes, setRows, setGrid, setActiveStopwatches, setRecordedTimes, setCurrentTimes, setLapTimes, setStartTimes, setLapStartTimes, setUnknownTimes, setIsSetupMode]);

    return {
        initializeGrid,
        startRow,
        stopRow,
        toggleStopwatch,
        restartStopwatch,
        recordTime,
        selectAthlete,
        handleSwapAthlete,
        recordUnknownTime,
        assignTimeToAthlete,
        handleSaveSession,
        handleLoadSession,
        handleDeleteSession,
        handleUpdateCurrentSession,
        handleManualSave,
        handleClearSavedState,
        handleResetToSetup,
        handleEditCell,
        handleLoadTemporaryState
    };
};
